import React from 'react';
import Svg from './Svg';

type FileIconProps = {
  name: string;
  isDirectory: boolean;
  encrypted?: boolean;
  size?: number;
};

// Color per file extension. Keep parity with the ray prototype.
const EXT_COLOR: Record<string, string> = {
  md: '#519aba',
  ts: '#3178c6',
  js: '#f0db4f',
  json: '#a9a038',
  css: '#56b3b4',
  html: '#e44d26',
  svg: '#e69d2a',
  png: '#26a269',
  jpg: '#26a269',
  txt: '#8b8b8b',
  py: '#3572A5',
  sh: '#89e051',
  ray: '#c084fc',
};

const FolderIcon: React.FC<{size: number}> = ({size}) => (
  <Svg size={size} viewBox="0 0 16 16" fill="#8da0cb">
    <path d="M1.5 1A1.5 1.5 0 000 2.5v11A1.5 1.5 0 001.5 15h13a1.5 1.5 0 001.5-1.5V4.5A1.5 1.5 0 0014.5 3H7.71L6.15 1.44A1.5 1.5 0 005.06 1H1.5z" />
  </Svg>
);

const GenericFileIcon: React.FC<{size: number; color: string}> = ({size, color}) => (
  <Svg size={size} viewBox="0 0 16 16" fill={color}>
    <path d="M3.5 1A1.5 1.5 0 002 2.5v11A1.5 1.5 0 003.5 15h9a1.5 1.5 0 001.5-1.5V6l-4.5-5H3.5zM10 1.5L13.5 5.5H10.5A.5.5 0 0110 5V1.5z" />
  </Svg>
);

const EncryptedIcon: React.FC<{size: number}> = ({size}) => (
  <Svg size={size} fill="#fb923c">
    <path d="M400 416C497.2 416 576 337.2 576 240C576 142.8 497.2 64 400 64C302.8 64 224 142.8 224 240C224 258.7 226.9 276.8 232.3 293.7L71 455C66.5 459.5 64 465.6 64 472L64 552C64 565.3 74.7 576 88 576L168 576C181.3 576 192 565.3 192 552L192 512L232 512C245.3 512 256 501.3 256 488L256 448L296 448C302.4 448 308.5 445.5 313 441L346.3 407.7C363.2 413.1 381.3 416 400 416zM440 160C462.1 160 480 177.9 480 200C480 222.1 462.1 240 440 240C417.9 240 400 222.1 400 200C400 177.9 417.9 160 440 160z" />
  </Svg>
);

export const FileIcon: React.FC<FileIconProps> = ({name, isDirectory, encrypted, size = 16}) => {
  if (encrypted) return <EncryptedIcon size={size} />;
  if (isDirectory) return <FolderIcon size={size} />;
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  const color = EXT_COLOR[ext] || '#8b8b8b';
  return <GenericFileIcon size={size} color={color} />;
};

// ---- Access level icon ----

type AccessLevel = 'public' | 'local' | 'private' | 'npc' | 'player' | 'everyone';

export const AccessIcon: React.FC<{level?: AccessLevel; size?: number}> = ({
  level = 'public',
  size = 12,
}) => {
  switch (level) {
    case 'local':
      return (
        <Svg size={size} viewBox="0 0 512 512" fill="#f87171">
          <path d="M256 8C119.034 8 8 119.033 8 256s111.034 248 248 248 248-111.034 248-248S392.967 8 256 8zm130.108 117.892c65.448 65.448 70 165.481 20.677 235.637L150.47 105.216c70.204-49.356 170.226-44.735 235.638 20.676zM125.892 386.108c-65.448-65.448-70-165.481-20.677-235.637L361.53 406.784c-70.203 49.356-170.226 44.736-235.638-20.676z" />
        </Svg>
      );
    case 'private':
      return (
        <Svg size={size} fill="#fb923c">
          <path d="M256 160L256 224L384 224L384 160C384 124.7 355.3 96 320 96C284.7 96 256 124.7 256 160zM192 224L192 160C192 89.3 249.3 32 320 32C390.7 32 448 89.3 448 160L448 224C483.3 224 512 252.7 512 288L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 288C128 252.7 156.7 224 192 224z" />
        </Svg>
      );
    case 'npc':
      return (
        <Svg size={size} viewBox="-16 -16 672 672" fill="rgba(255,255,255,0.2)">
          <path d="M352 64C352 46.3 337.7 32 320 32C302.3 32 288 46.3 288 64L288 128L192 128C139 128 96 171 96 224L96 448C96 501 139 544 192 544L448 544C501 544 544 501 544 448L544 224C544 171 501 128 448 128L352 128L352 64zM160 432C160 418.7 170.7 408 184 408L216 408C229.3 408 240 418.7 240 432C240 445.3 229.3 456 216 456L184 456C170.7 456 160 445.3 160 432zM280 432C280 418.7 290.7 408 304 408L336 408C349.3 408 360 418.7 360 432C360 445.3 349.3 456 336 456L304 456C290.7 456 280 445.3 280 432zM400 432C400 418.7 410.7 408 424 408L456 408C469.3 408 480 418.7 480 432C480 445.3 469.3 456 456 456L424 456C410.7 456 400 445.3 400 432zM224 240C250.5 240 272 261.5 272 288C272 314.5 250.5 336 224 336C197.5 336 176 314.5 176 288C176 261.5 197.5 240 224 240zM368 288C368 261.5 389.5 240 416 240C442.5 240 464 261.5 464 288C464 314.5 442.5 336 416 336C389.5 336 368 314.5 368 288zM64 288C64 270.3 49.7 256 32 256C14.3 256 0 270.3 0 288L0 384C0 401.7 14.3 416 32 416C49.7 416 64 401.7 64 384L64 288zM608 256C590.3 256 576 270.3 576 288L576 384C576 401.7 590.3 416 608 416C625.7 416 640 401.7 640 384L640 288C640 270.3 625.7 256 608 256z" />
        </Svg>
      );
    case 'player':
      return (
        <Svg size={size} fill="rgba(255,255,255,0.2)">
          <path d="M320 312C386.3 312 440 258.3 440 192C440 125.7 386.3 72 320 72C253.7 72 200 125.7 200 192C200 258.3 253.7 312 320 312zM290.3 368C191.8 368 112 447.8 112 546.3C112 562.7 125.3 576 141.7 576L498.3 576C514.7 576 528 562.7 528 546.3C528 447.8 448.2 368 349.7 368L290.3 368z" />
        </Svg>
      );
    case 'everyone':
      return (
        <Svg size={size} viewBox="-16 -16 672 672" fill="#fb923c">
          <path d="M320 80C377.4 80 424 126.6 424 184C424 241.4 377.4 288 320 288C262.6 288 216 241.4 216 184C216 126.6 262.6 80 320 80zM96 152C135.8 152 168 184.2 168 224C168 263.8 135.8 296 96 296C56.2 296 24 263.8 24 224C24 184.2 56.2 152 96 152zM0 480C0 409.3 57.3 352 128 352C140.8 352 153.2 353.9 164.9 357.4C132 394.2 112 442.8 112 496L112 512C112 523.4 114.4 534.2 118.7 544L32 544C14.3 544 0 529.7 0 512L0 480zM521.3 544C525.6 534.2 528 523.4 528 512L528 496C528 442.8 508 394.2 475.1 357.4C486.8 353.9 499.2 352 512 352C582.7 352 640 409.3 640 480L640 512C640 529.7 625.7 544 608 544L521.3 544zM472 224C472 184.2 504.2 152 544 152C583.8 152 616 184.2 616 224C616 263.8 583.8 296 544 296C504.2 296 472 263.8 472 224zM160 496C160 407.6 231.6 336 320 336C408.4 336 480 407.6 480 496L480 512C480 529.7 465.7 544 448 544L192 544C174.3 544 160 529.7 160 512L160 496z" />
        </Svg>
      );
    case 'public':
    default:
      return (
        <Svg size={size} fill="rgba(255,255,255,0.2)">
          <path d="M415.9 344L225 344C227.9 408.5 242.2 467.9 262.5 511.4C273.9 535.9 286.2 553.2 297.6 563.8C308.8 574.3 316.5 576 320.5 576C324.5 576 332.2 574.3 343.4 563.8C354.8 553.2 367.1 535.8 378.5 511.4C398.8 467.9 413.1 408.5 416 344zM224.9 296L415.8 296C413 231.5 398.7 172.1 378.4 128.6C367 104.2 354.7 86.8 343.3 76.2C332.1 65.7 324.4 64 320.4 64C316.4 64 308.7 65.7 297.5 76.2C286.1 86.8 273.8 104.2 262.4 128.6C242.1 172.1 227.8 231.5 224.9 296zM176.9 296C180.4 210.4 202.5 130.9 234.8 78.7C142.7 111.3 74.9 195.2 65.5 296L176.9 296zM65.5 344C74.9 444.8 142.7 528.7 234.8 561.3C202.5 509.1 180.4 429.6 176.9 344L65.5 344zM463.9 344C460.4 429.6 438.3 509.1 406 561.3C498.1 528.6 565.9 444.8 575.3 344L463.9 344zM575.3 296C565.9 195.2 498.1 111.3 406 78.7C438.3 130.9 460.4 210.4 463.9 296L575.3 296z" />
        </Svg>
      );
  }
};
